import { useEffect } from 'react';
import { useBroker } from '../contexts/BrokerContext';
import { useLocale } from '../contexts/LocaleContext';
import { 
  Users, Wallet, TrendingUp, DollarSign, ArrowUpRight, Check, X, Zap, ChevronRight,
  TrendingDown, AlertTriangle, Activity
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { reportsAPI } from '../services/api';
import toast from 'react-hot-toast';

// ─── Mobile detection ─────────────────────────────────────────────────────────
import { useState } from 'react';
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtUSD = (n) => '$' + (n != null ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

// ─── Circular usage ring ──────────────────────────────────────────────────────
function UsageRing({ used, limit, label, color }) {
  const isUnlimited = limit >= 99999;
  const pct = isUnlimited ? 100 : Math.min((used / limit) * 100, 100);
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDash = `${(pct / 100) * circumference} ${circumference}`;

  let strokeColor = color;
  if (!isUnlimited) {
    if (pct >= 90) strokeColor = 'var(--accent-danger)';
    else if (pct >= 70) strokeColor = 'var(--accent-warning)';
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-[72px] h-[72px]">
        <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
          <circle cx="36" cy="36" r={radius} fill="none" stroke="var(--border-color)" strokeWidth="6" />
          <circle cx="36" cy="36" r={radius} fill="none" stroke={strokeColor} strokeWidth="6"
            strokeLinecap="round" strokeDasharray={strokeDash}
            style={{ transition: 'stroke-dasharray 0.6s ease, stroke 0.3s ease' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs font-bold font-mono" style={{ color: strokeColor }}>
            {isUnlimited ? '∞' : used}
          </span>
        </div>
      </div>
      <div className="text-center">
        <p className="text-xs font-semibold text-[var(--text-primary)]">{label}</p>
        <p className="text-[10px] text-[var(--text-tertiary)] font-mono">
          {isUnlimited ? `${used} used` : `${used} / ${limit}`}
        </p>
      </div>
    </div>
  );
}

// ─── Market badge ─────────────────────────────────────────────────────────────
function MarketBadge({ label, allowed }) {
  return (
    <div
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
      style={{
        backgroundColor: allowed ? 'var(--accent-success)15' : 'var(--bg-tertiary)',
        border: `1px solid ${allowed ? 'var(--accent-success)40' : 'var(--border-color)'}`,
        color: allowed ? 'var(--accent-success)' : 'var(--text-tertiary)',
      }}
    >
      {allowed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {label}
    </div>
  );
}

// ─── Mini bar ─────────────────────────────────────────────────────────────────
function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color, transition: 'width 0.5s ease' }} />
    </div>
  );
}

// ─── Mobile stat chip ─────────────────────────────────────────────────────────
function StatChip({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--text-tertiary)]" />
      </div>
      <div>
        <p className="text-xl font-black font-mono text-[var(--text-primary)] leading-none">{value}</p>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">{label}</p>
        {sub && <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Mobile section card ──────────────────────────────────────────────────────
function MobileCard({ title, children, className = '' }) {
  return (
    <div className={`bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl overflow-hidden ${className}`}>
      {title && (
        <div className="px-4 py-3 border-b border-[var(--border-color)]">
          <p className="text-xs font-bold uppercase tracking-wide text-[var(--text-secondary)]">{title}</p>
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

// ─── Financial stat card ──────────────────────────────────────────────────────
function FinancialCard({ icon: Icon, label, value, subtext, color, trend }) {
  return (
    <div className="p-4 sm:p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>{label}</p>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <p className="text-xl sm:text-2xl font-bold font-mono mb-0.5" style={{ color }}>{value}</p>
      {subtext && <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{subtext}</p>}
      {trend && (
        <div className="mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{trend}</p>
        </div>
      )}
    </div>
  );
}

// ─── Simple bar chart component ───────────────────────────────────────────────
function SimpleBarChart({ data, maxValue }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 sm:h-40 text-xs text-[var(--text-tertiary)]">
        No data available
      </div>
    );
  }
  
  // Ensure we have a valid max value
  const safeMaxValue = maxValue > 0 ? maxValue : Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="flex items-end gap-2 sm:gap-3 h-32 sm:h-40 px-2">
      {data.map((item, idx) => {
        const height = safeMaxValue > 0 ? (item.value / safeMaxValue) * 100 : 0;
        const displayHeight = Math.max(height, 2); // Minimum 2% height for visibility
        
        return (
          <div key={idx} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full flex items-end justify-center relative group" style={{ height: '100%' }}>
              <div 
                className="w-full rounded-t transition-all duration-500 hover:opacity-80 relative"
                style={{ 
                  height: `${displayHeight}%`,
                  backgroundColor: item.color || 'var(--accent-primary)',
                  minHeight: item.value > 0 ? '8px' : '2px',
                  cursor: 'pointer'
                }}
              >
                {/* Tooltip on hover */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded bg-gray-900 text-white text-[10px] font-mono whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  {item.displayValue || item.value}
                </div>
              </div>
            </div>
            <p className="text-[9px] sm:text-[10px] font-semibold text-center leading-tight" 
              style={{ color: 'var(--text-tertiary)' }}>
              {item.label}
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ─── View Details Link ────────────────────────────────────────────────────────
function ViewDetailsLink({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 text-xs font-semibold hover:underline transition-all"
      style={{ color: 'var(--accent-primary)' }}
    >
      View Details
      <ArrowUpRight className="w-3 h-3" />
    </button>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const isMobile = useIsMobile();
  const { dashboardStats, fetchDashboardStats, loading } = useBroker();
  const navigate = useNavigate();
  const { isInGrace } = useSubscription();
  const { t } = useLocale();

  const [reportData, setReportData] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);

  useEffect(() => { 
    fetchDashboardStats(); 
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const dateRange = {
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
      };
      const dashboard = await reportsAPI.getDashboard(dateRange);
      setReportData(dashboard.data);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoadingReports(false);
    }
  };

  if (loading || loadingReports) {
    return <div className="text-center py-12">{t('common.loading')}</div>;
  }

  const d = dashboardStats || {};
  const plan = d.plan || {};
  const usage = d.usage || {};
  const markets = d.allowedMarkets || {};
  const upgrade = d.upgrade || null;

  const { financial, trading } = reportData || {};
  const deposits = financial?.deposits || {};
  const withdrawals = financial?.withdrawals || {};

  const stats = [
    { icon: Users, label: t('dashboard.totalClients'), value: d.clients?.total || 0, sub: `+${d.clients?.newToday || 0} today`, color: 'var(--accent-primary)' },
    { icon: Wallet, label: t('dashboard.totalAccounts'), value: d.accounts?.total || 0, sub: `${d.accounts?.live || 0} live / ${d.accounts?.demo || 0} demo`, color: 'var(--accent-info)' },
    { icon: TrendingUp, label: t('dashboard.openPositions'), value: d.trading?.openPositions || 0, sub: `${d.trading?.tradesToday || 0} trades today`, color: 'var(--accent-success)' },
    { icon: DollarSign, label: t('dashboard.todayPnL'), value: `$${(d.financial?.pnlToday || 0).toLocaleString()}`, sub: null, color: d.financial?.pnlToday >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' },
  ];

  const usageItems = [
    { label: 'Traders', used: usage.traders?.used, limit: usage.traders?.limit, color: 'var(--accent-primary)' },
    { label: 'Admins', used: usage.admins?.used, limit: usage.admins?.limit, color: 'var(--accent-info)' },
    { label: 'Instruments', used: usage.instruments?.used, limit: usage.instruments?.limit, color: 'var(--accent-success)' },
  ];

  // Chart data for financial overview
  const financialChartData = [
    { label: 'Deposits', value: parseFloat(deposits.total || 0), displayValue: fmtUSD(deposits.total), color: 'var(--accent-success)' },
    { label: 'Withdrawals', value: parseFloat(withdrawals.total || 0), displayValue: fmtUSD(withdrawals.total), color: 'var(--accent-danger)' },
    { label: 'Net Flow', value: Math.abs(parseFloat(financial?.net_cash_flow || 0)), displayValue: fmtUSD(financial?.net_cash_flow), color: financial?.net_cash_flow >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' },
  ];

  const maxFinancialValue = Math.max(...financialChartData.map(d => d.value), 1);

  // Risk alerts data
  const hasRiskAlerts = (trading?.accounts_at_stopout > 0 || trading?.accounts_at_margin_call > 0 || trading?.accounts_high_risk > 0);
  const riskChartData = [
    { label: 'Stop Out', value: trading?.accounts_at_stopout || 0, color: 'var(--accent-danger)' },
    { label: 'Margin Call', value: trading?.accounts_at_margin_call || 0, color: 'var(--accent-warning)' },
    { label: 'High Risk', value: trading?.accounts_high_risk || 0, color: 'var(--accent-warning)' },
  ];
  const maxRiskValue = Math.max(...riskChartData.map(d => d.value), 1);

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-1">{t('dashboard.title')}</p>
          <p className="text-4xl font-black text-white leading-none font-mono">
            ${(d.financial?.pnlToday || 0).toLocaleString()}
          </p>
          <p className="text-white/60 text-xs mt-1.5">{t('dashboard.todayPnL')}</p>

          {/* Plan pill + upgrade */}
          <div className="flex items-center gap-2 mt-4">
            {plan.name && (
              <span className="bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/25">
                {plan.name}
              </span>
            )}
            {upgrade && (
              <button 
                onClick={() => navigate('/upgrade')}
                className="flex items-center gap-1 bg-white text-[11px] font-bold px-2.5 py-1 rounded-full"
                style={{ color: 'var(--accent-primary)' }}>
                <Zap className="w-3 h-3" />
                Upgrade to {upgrade.name}
                <ArrowUpRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* ── Stat grid ────────────────────────────────────────────────── */}
        <div className="px-4 pt-4 grid grid-cols-2 gap-3">
          {stats.map((s, i) => (
            <StatChip key={i} {...s} />
          ))}
        </div>

        <div className="px-4 pt-4 space-y-3 pb-8">

          {/* ── Financial Overview ─────────────────────────────────────── */}
          <MobileCard title="Financial Overview (30 Days)">
            <div className="flex items-center justify-end mb-3">
              <ViewDetailsLink onClick={() => navigate('/reports')} />
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-xs text-[var(--text-tertiary)] mb-1">Deposits</p>
                <p className="text-lg font-bold font-mono" style={{ color: 'var(--accent-success)' }}>
                  {fmtUSD(deposits.total)}
                </p>
                <p className="text-[10px] text-[var(--text-tertiary)]">{deposits.count || 0} transactions</p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-tertiary)] mb-1">Withdrawals</p>
                <p className="text-lg font-bold font-mono" style={{ color: 'var(--accent-danger)' }}>
                  {fmtUSD(withdrawals.total)}
                </p>
                <p className="text-[10px] text-[var(--text-tertiary)]">{withdrawals.count || 0} transactions</p>
              </div>
            </div>
            <SimpleBarChart data={financialChartData} maxValue={maxFinancialValue} />
            <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[var(--text-tertiary)]">Net Cash Flow</span>
                <span className="text-sm font-bold font-mono" 
                  style={{ color: financial?.net_cash_flow >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                  {fmtUSD(financial?.net_cash_flow)}
                </span>
              </div>
            </div>
          </MobileCard>

          {/* ── Risk Alerts ────────────────────────────────────────────── */}
          {hasRiskAlerts && (
            <MobileCard title="Risk Alerts">
              <div className="flex items-center justify-end mb-3">
                <ViewDetailsLink onClick={() => navigate('/reports')} />
              </div>
              
              <div className="grid grid-cols-3 gap-2 mb-3">
                {riskChartData.map((item, idx) => (
                  <div key={idx} className="text-center">
                    <p className="text-2xl font-black font-mono" style={{ color: item.color }}>{item.value}</p>
                    <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{item.label}</p>
                  </div>
                ))}
              </div>
              <SimpleBarChart data={riskChartData} maxValue={maxRiskValue} />
            </MobileCard>
          )}

          {/* ── Plan details ─────────────────────────────────────────── */}
          <MobileCard title="Current Plan">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-2xl font-black font-mono text-[var(--text-primary)]">{plan.name || '—'}</p>
                <p className="text-xs text-[var(--text-tertiary)]">
                  <span className="font-bold font-mono" style={{ color: 'var(--accent-primary)' }}>${plan.price?.toLocaleString() || 0}</span>
                  /{plan.billingCycle || 'mo'}
                </p>
              </div>
              <span
                className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider"
                style={{
                  backgroundColor: plan.status === 'active' ? 'var(--accent-success)18' : 'var(--accent-danger)18',
                  color: plan.status === 'active' ? 'var(--accent-success)' : 'var(--accent-danger)',
                }}
              >
                {plan.status || '—'}
              </span>
            </div>
            <div className="space-y-2 border-t border-[var(--border-color)] pt-3">
              {[
                ['Feed Latency', plan.feedLatency || '—', null],
                ['API Access', plan.apiAccess ? 'Enabled' : 'Disabled', plan.apiAccess ? 'var(--accent-success)' : 'var(--accent-danger)'],
                ['Billing', plan.billingCycle || '—', null],
                ['Expires', plan.expiresAt ? new Date(plan.expiresAt).toLocaleDateString() : '—', null],
              ].map(([label, val, col]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-[var(--text-tertiary)]">{label}</span>
                  <span className="text-xs font-bold font-mono text-[var(--text-primary)]" style={col ? { color: col } : {}}>{val}</span>
                </div>
              ))}
            </div>
          </MobileCard>

          {/* ── Usage ────────────────────────────────────────────────── */}
          <MobileCard title="Plan Usage">
            <div className="flex justify-around mb-4">
              {usageItems.map(item => (
                <UsageRing key={item.label} used={item.used || 0} limit={item.limit || 0} label={item.label} color={item.color} />
              ))}
            </div>
            <div className="space-y-3">
              {usageItems.map(item => {
                const isUnlimited = (item.limit || 0) >= 99999;
                return (
                  <div key={item.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-[var(--text-secondary)]">{item.label}</span>
                      <span className="font-mono text-[var(--text-tertiary)]">
                        {isUnlimited ? `${item.used || 0} / ∞` : `${item.used || 0} / ${item.limit || 0}`}
                      </span>
                    </div>
                    <MiniBar value={item.used || 0} max={isUnlimited ? (item.used || 1) : (item.limit || 1)} color={item.color} />
                  </div>
                );
              })}
            </div>
          </MobileCard>

          {/* ── Markets ──────────────────────────────────────────────── */}
          <MobileCard title="Allowed Markets">
            <div className="flex flex-wrap gap-2">
              <MarketBadge label="Forex" allowed={markets.forex} />
              <MarketBadge label="Commodities" allowed={markets.commodities} />
              <MarketBadge label="Indices" allowed={markets.indices} />
              <MarketBadge label="Stocks" allowed={markets.stocks} />
              <MarketBadge label="Crypto" allowed={markets.crypto} />
            </div>
          </MobileCard>

          {/* ── Upgrade CTA ──────────────────────────────────────────── */}
          {upgrade && (
            <div className="rounded-2xl p-5 relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, var(--accent-primary)18 0%, var(--accent-info)10 100%)',
              border: '1px solid var(--accent-primary)35',
            }}>
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full blur-xl opacity-30"
                style={{ backgroundColor: 'var(--accent-primary)' }} />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent-primary)' }}>
                    Upgrade Available
                  </span>
                </div>
                <p className="text-sm font-bold text-[var(--text-primary)] mb-1">{upgrade.name} Plan</p>
                <p className="text-xs text-[var(--text-tertiary)] mb-4">
                  Unlock {upgrade.feedLatency} feeds{upgrade.apiAccess ? ', API access' : ''} & more
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-bold font-mono text-[var(--text-primary)]">
                    ${upgrade.price?.toLocaleString()}<span className="text-xs font-normal text-[var(--text-tertiary)]">/mo</span>
                  </span>
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all active:scale-95"
                    style={{ backgroundColor: isInGrace ? 'var(--accent-danger)' : 'var(--accent-primary)' }}>
                    {isInGrace ? 'Renew' : 'Upgrade'} <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">{t('dashboard.title')}</h1>
        <p className="text-[var(--text-secondary)]">{t('dashboard.overview')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="p-6 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}20` }}>
                <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
              </div>
            </div>
            <h3 className="text-2xl font-bold mb-1 font-mono">{stat.value}</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-2">{stat.label}</p>
            {stat.sub && <p className="text-xs text-[var(--text-tertiary)]">{stat.sub}</p>}
          </div>
        ))}
      </div>

      {/* Financial Overview */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" style={{ color: 'var(--accent-success)' }} />
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              Financial Overview (Last 30 Days)
            </h2>
          </div>
          <ViewDetailsLink onClick={() => navigate('/reports')} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="grid grid-cols-2 gap-4">
            <FinancialCard
              icon={TrendingUp}
              label="Total Deposits"
              value={fmtUSD(deposits.total)}
              subtext={`${deposits.count || 0} transactions · ${deposits.unique_users || 0} users`}
              color="var(--accent-success)"
              trend={`Avg: ${fmtUSD(deposits.average)} · Approval: ${deposits.approval_rate || 0}%`}
            />
            <FinancialCard
              icon={TrendingDown}
              label="Total Withdrawals"
              value={fmtUSD(withdrawals.total)}
              subtext={`${withdrawals.count || 0} transactions · ${withdrawals.unique_users || 0} users`}
              color="var(--accent-danger)"
              trend={`Avg: ${fmtUSD(withdrawals.average)} · Approval: ${withdrawals.approval_rate || 0}%`}
            />
            <FinancialCard
              icon={DollarSign}
              label="Net Cash Flow"
              value={fmtUSD(financial?.net_cash_flow)}
              subtext={`Ratio: ${financial?.deposit_withdrawal_ratio || '0.00'}:1`}
              color={financial?.net_cash_flow >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)'}
            />
            <FinancialCard
              icon={Activity}
              label="Total Volume"
              value={fmtUSD(parseFloat(deposits.total || 0) + parseFloat(withdrawals.total || 0))}
              subtext={`${(deposits.count || 0) + (withdrawals.count || 0)} total transactions`}
              color="var(--accent-info)"
            />
          </div>
          <div className="p-6 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Cash Flow Comparison
            </h3>
            <SimpleBarChart data={financialChartData} maxValue={maxFinancialValue} />
          </div>
        </div>
      </div>

      {/* Risk Alerts */}
      {hasRiskAlerts && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--accent-danger)' }} />
              <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Risk Alerts</h2>
            </div>
            <ViewDetailsLink onClick={() => navigate('/reports')} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="grid grid-cols-3 gap-4">
              {riskChartData.map((item, idx) => (
                <div key={idx} className="p-4 rounded-xl border"
                  style={{
                    backgroundColor: `color-mix(in srgb, ${item.color} 8%, transparent)`,
                    borderColor: `color-mix(in srgb, ${item.color} 25%, transparent)`
                  }}>
                  <p className="text-xs font-semibold mb-2" style={{ color: item.color }}>{item.label}</p>
                  <p className="text-3xl font-bold font-mono" style={{ color: item.color }}>{item.value}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>accounts</p>
                </div>
              ))}
            </div>
            <div className="p-6 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
              <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Risk Distribution</h3>
              <SimpleBarChart data={riskChartData} maxValue={maxRiskValue} />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Plan */}
        <div className="p-6 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold text-[var(--text-primary)]">Current Plan</h3>
            <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider"
              style={{
                backgroundColor: plan.status === 'active' ? 'var(--accent-success)18' : 'var(--accent-danger)18',
                color: plan.status === 'active' ? 'var(--accent-success)' : 'var(--accent-danger)',
              }}>
              {plan.status || '—'}
            </span>
          </div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-3xl font-bold font-mono text-[var(--text-primary)]">{plan.name || '—'}</span>
            <span className="text-sm text-[var(--text-tertiary)]">plan</span>
          </div>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-xl font-bold font-mono" style={{ color: 'var(--accent-primary)' }}>
              ${plan.price?.toLocaleString() || 0}
            </span>
            <span className="text-xs text-[var(--text-tertiary)]">/{plan.billingCycle || 'mo'}</span>
          </div>
          <div className="space-y-2.5">
            {[
              ['Feed Latency', plan.feedLatency || '—', null],
              ['API Access', plan.apiAccess ? 'Enabled' : 'Disabled', plan.apiAccess ? 'var(--accent-success)' : 'var(--accent-danger)'],
              ['Billing', plan.billingCycle || '—', null],
              ['Expires', plan.expiresAt ? new Date(plan.expiresAt).toLocaleDateString() : '—', null],
            ].map(([label, val, col]) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-[var(--text-tertiary)]">{label}</span>
                <span className="font-semibold text-[var(--text-primary)]" style={col ? { color: col } : {}}>{val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Usage Rings */}
        <div className="p-6 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)]">
          <h3 className="font-semibold text-[var(--text-primary)] mb-5">Plan Usage</h3>
          <div className="flex justify-around items-start mb-6">
            {usageItems.map(item => (
              <UsageRing key={item.label} used={item.used || 0} limit={item.limit || 0} label={item.label} color={item.color} />
            ))}
          </div>
          <div className="space-y-3">
            {usageItems.map(item => {
              const isUnlimited = (item.limit || 0) >= 99999;
              return (
                <div key={item.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[var(--text-secondary)]">{item.label}</span>
                    <span className="font-mono text-[var(--text-tertiary)]">
                      {isUnlimited ? `${item.used || 0} / ∞` : `${item.used || 0} / ${item.limit || 0}`}
                    </span>
                  </div>
                  <MiniBar value={item.used || 0} max={isUnlimited ? (item.used || 1) : (item.limit || 1)} color={item.color} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Markets + Upgrade */}
        <div className="flex flex-col gap-6">
          <div className="p-6 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] flex-1">
            <h3 className="font-semibold text-[var(--text-primary)] mb-4">Allowed Markets</h3>
            <div className="flex flex-wrap gap-2">
              <MarketBadge label="Forex" allowed={markets.forex} />
              <MarketBadge label="Commodities" allowed={markets.commodities} />
              <MarketBadge label="Indices" allowed={markets.indices} />
              <MarketBadge label="Stocks" allowed={markets.stocks} />
              <MarketBadge label="Crypto" allowed={markets.crypto} />
            </div>
          </div>
          {upgrade && (
            <div className="p-5 rounded-xl relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, var(--accent-primary)18 0%, var(--accent-info)10 100%)', border: '1px solid var(--accent-primary)35' }}>
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full blur-xl opacity-30" style={{ backgroundColor: 'var(--accent-primary)' }} />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent-primary)' }}>Upgrade Available</span>
                </div>
                <p className="text-sm font-bold text-[var(--text-primary)] mb-1">{upgrade.name} Plan</p>
                <p className="text-xs text-[var(--text-tertiary)] mb-3">
                  Unlock {upgrade.feedLatency} feeds{upgrade.apiAccess ? ', API access' : ''} & more
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-bold font-mono text-[var(--text-primary)]">
                    ${upgrade.price?.toLocaleString()}<span className="text-xs font-normal text-[var(--text-tertiary)]">/mo</span>
                  </span>
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90 active:scale-95"
                    style={{ backgroundColor: isInGrace ? 'var(--accent-danger)' : 'var(--accent-primary)' }}>
                    {isInGrace ? 'Renew' : 'Upgrade'} <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}